import React, { useContext, useEffect, useMemo } from 'react'

import { useGlobalExperiencesSearchContext, useGlobalExperiencesSearchResultsTracking } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalExperiencesSearch'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { fetchExperienceCategories } from 'actions/ExperienceActions'
import useQueryParams from 'hooks/useQueryParams'
import { useAppDispatch } from 'hooks/reduxHooks'
import Places, { getPlaceByRegionCode } from 'constants/places'
import ExperienceSearchMap from './ExperienceSearchMap'
import { deleteSearchParams } from 'lib/url/searchUrlUtils'
import { connect } from 'react-redux'
import ExperienceSearchPageContainer from './ExperienceSearchPageContainer'
import useExperiencesSearchFilters from '../../Experiences/hooks/useExperiencesSearchFilters'
import { usePlaceAncestorsById } from 'hooks/usePlace'
import { take, without } from 'lib/array/arrayUtils'
import GeoContext from 'contexts/geoContext'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import useExperiences from 'hooks/Experiences/useExperiences'

interface MapPageProps {
  search: string;
}

interface Props extends MapPageProps {}

function ExperiencesSearchPage({ search }:Props) {
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const geo = useContext(GeoContext)

  const { globalExperiencesSearchState } = useGlobalExperiencesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
  }, queryParams)

  const placeId = queryParams.get('destinationId')
  const {
    searchFilters,
  } = useExperiencesSearchFilters()

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const regionPlace = useMemo(() => getPlaceByRegionCode(geo.currentRegionCode), [geo.currentRegionCode])

  const [ancestorPlaceIds, fetchingAncestors] = usePlaceAncestorsById(placeId)

  const fallbackPlaceIds = useMemo(() => {
    const ancestorIds = without(ancestorPlaceIds, Places.Anywhere.id)
    return [...ancestorIds, regionPlace.id, Places.Anywhere.id]
  }, [ancestorPlaceIds, regionPlace])

  const list = useExperienceSearchList({
    placeId,
    fallbackPlaceIds: fetchingAncestors ? [] : fallbackPlaceIds,
    categoryCodes: searchFilters.categoryCodes,
    priceLte: searchFilters.priceLte,
    priceGte: searchFilters.priceGte,
    sortBy: 'type',
  })

  const limit = useMemo(() => take(list.ids, 100), [list.ids])

  const [experiences, fetching] = useExperiences(limit)

  useGlobalExperiencesSearchResultsTracking(globalExperiencesSearchState, list.ids, list.fetching)

  const searchMode = useMemo(() => ({
    url: `/search/experiences?${deleteSearchParams(search, 'sortBy')}`,
    mode: 'map',
  }), [search])

  return <ExperienceSearchPageContainer
    searchMode={searchMode}
    list={list}
    >
    <ExperienceSearchMap
      experiences={experiences}
      fetching={fetching}
    />
  </ExperienceSearchPageContainer>
}

const mapStateToProps = (state: App.State) => ({
  search: state.router.location.search,
})

export default connect(mapStateToProps)(ExperiencesSearchPage)
